import React from 'react';
import { useLocation } from 'react-router-dom';
import trackEvent from '../utils/analytics';

const BUY_ME_A_COFFEE_URL = 'https://buymeacoffee.com/ammarabuyahya';
const SHOW_DELAY_MS = 60000;
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const SESSION_STORAGE_KEY = 'coffeeSupportBannerShown';
const DISMISSED_AT_STORAGE_KEY = 'coffeeSupportBannerDismissedAt';

function hasActiveDismissalCooldown() {
  try {
    const dismissedAt = Number(localStorage.getItem(DISMISSED_AT_STORAGE_KEY));
    return Number.isFinite(dismissedAt) && dismissedAt > 0 && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS;
  } catch (_) {
    return false;
  }
}

function markBannerDismissed() {
  try {
    localStorage.setItem(DISMISSED_AT_STORAGE_KEY, String(Date.now()));
  } catch (_) {
    // Storage may be unavailable in private browsing. The banner should still close.
  }
}

function wasShownThisSession() {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
  } catch (_) {
    return false;
  }
}

function markShownThisSession() {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
  } catch (_) {
    // A blocked storage API should not prevent the support prompt from working.
  }
}

export default function CoffeeSupportBanner() {
  const location = useLocation();
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (location.pathname === '/' && window.innerWidth <= 767) {
      setVisible(false);
      return;
    }

    if (wasShownThisSession() || hasActiveDismissalCooldown()) {
      return;
    }

    const timer = setTimeout(() => {
      markShownThisSession();
      setVisible(true);
      trackEvent('coffee_support_impression', { placement: 'global_banner' });
    }, SHOW_DELAY_MS);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  if (!visible) {
    return null;
  }

  const dismiss = () => {
    markBannerDismissed();
    setVisible(false);
  };

  const support = () => {
    trackEvent('coffee_support_click', { placement: 'global_banner' });
    dismiss();
  };

  return (
    <div
      role="complementary"
      aria-label="Support Squadverse"
      aria-live="polite"
      className="coffee-support-banner"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        padding: '0.85rem 1rem calc(0.85rem + env(safe-area-inset-bottom))',
        boxSizing: 'border-box',
        background: 'rgba(15, 23, 42, 0.97)',
        borderTop: '1px solid rgba(251, 191, 36, 0.35)',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.35)',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.75rem',
        fontFamily: 'var(--sv2-font)'
      }}
    >
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        <p style={{ margin: 0, color: '#e2e8f0', fontSize: '0.86rem', fontWeight: 700, lineHeight: 1.35 }}>
          Enjoying Squadverse?
        </p>
        <p style={{ margin: '0.15rem 0 0', color: '#94a3b8', fontSize: '0.78rem', lineHeight: 1.35 }}>
          Help keep Squadverse free and support new football games.
        </p>
      </div>
      <a
        href={BUY_ME_A_COFFEE_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={support}
        aria-label="Support Squadverse on Buy Me a Coffee"
        style={{
          flexShrink: 0,
          minHeight: 44,
          padding: '0.65rem 1rem',
          borderRadius: 'var(--sv2-radius-sm)',
          border: '1px solid #fbbf24',
          background: '#f59e0b',
          color: '#0f172a',
          textDecoration: 'none',
          fontSize: '0.84rem',
          fontWeight: 900,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          whiteSpace: 'nowrap'
        }}
      >
        Support Squadverse
      </a>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss support message for seven days"
        style={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: '50%',
          border: '1px solid rgba(148, 163, 184, 0.35)',
          background: 'transparent',
          color: '#94a3b8',
          fontSize: '1rem',
          lineHeight: 1,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0
        }}
      >
        ✕
      </button>
    </div>
  );
}
